import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { MarketTypes } from '../../api/types';
import useBoundStore from 'store';
import { createSaleAd, editSaleAd } from 'api';
import { BuyApartmentFlowPaths, SellApartmentFlowPaths } from 'routes';
import useApartmentSaleInfo, { APIApartmentSellInfoRes } from 'api/hooks/use-apartment-sale-info';
import { ruble } from '../../components/price-calculator';
import Switchbox from '../../components/switchbox';
import NumberInput from '../../components/number-input';
import BaseScreen from 'components/base-screen';
import BottomSheet from 'components/bottom-sheet';
import Button, { ButtonProps } from 'components/button';
import BottomSheetResult from 'components/bottom-sheet-result';
import ApartmentListItem from 'components/apartment-list-item';
import styles from './styles.module.scss';

export type InputApartmentPriceNavigateProps = {
  apartment: APIApartmentSellInfoRes & {
    id: number;
    price: number;
    hasMaternityCapital: boolean;
    hasBurden: boolean;
    minorOwners: boolean;
    loweredPrice: number;
  };
};

const AddAdditionalOptions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);
  const sellApartmentNumber = useBoundStore((s) => s.sellApartmentNumber);
  const resetSellFlow = useBoundStore((s) => s.resetSellFlow);
  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);

  const navigationProps = useLocation().state as InputApartmentPriceNavigateProps | undefined;

  const saleInfo = useApartmentSaleInfo(
    selectedSellBuilding.id,
    navigationProps ? '' : sellApartmentNumber,
  );

  const apartmentSellInfo = navigationProps?.apartment ?? saleInfo.apartmentSellInfo;

  const [maternalCapital, setMaternalCapital] = useState(
    apartmentSellInfo?.hasMaternityCapital ?? false,
  );
  const [encumbrance, setEncumbrance] = useState(apartmentSellInfo?.hasBurden ?? false);
  const [understatement, setUnderstatement] = useState(apartmentSellInfo?.loweredPrice ?? false);
  const [minorOwners, setMinorOwners] = useState(navigationProps?.apartment?.minorOwners);

  interface IPriceStat {
    days: number;
    price: number;
    percent: number;
  }

  const [selectedPrice, setSelectedPrice] = useState<{
    stat: IPriceStat;
    value: number;
  } | null>(null);

  const [priceWithoutMarketPrise, setPriceWithoutMarketPrise] = useState(
    apartmentSellInfo?.loweredPrice ? `${apartmentSellInfo?.loweredPrice}` : '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const buttons: ButtonProps[] = [
    navigationProps
      ? {
          label: 'Изменить',
          loading: isSubmitting,
          onClick: async () => {
            setIsSubmitting(true);
            const editAdRes = await editSaleAd(
              navigationProps.apartment.id,
              navigationProps.apartment.price,
              understatement ? +priceWithoutMarketPrise! : 0,
              maternalCapital,
              encumbrance,
              minorOwners,
            );
            setIsSubmitting(false);

            if (editAdRes.result) {
              navigate(
                state.marketTypes === MarketTypes.SECONDARY
                  ? SellApartmentFlowPaths.apartmentSaleAdsSecondary
                  : SellApartmentFlowPaths.apartmentSaleAds,
              );
            }
          },
        }
      : {
          label: 'Разместить объявление',
          loading: isSubmitting,
          disabled: !priceWithoutMarketPrise,
          onClick: async () => {
            if (!forceAuthorize()) return;

            setIsSubmitting(true);
            const submitRes = await createSaleAd({
              buildingId: selectedSellBuilding.id,
              apartmentNumber: sellApartmentNumber,
              price: saleInfo?.apartmentSellInfo
                ? Math.floor(selectedPrice?.stat?.price ?? 0)
                : Math.floor(+priceWithoutMarketPrise),
              expiresInDays: saleInfo?.apartmentSellInfo ? selectedPrice?.stat?.days : 40,
            });
            setIsSubmitting(false);

            if (submitRes.result) {
              resetSellFlow();
              setIsBottomSheetOpen(true);
            }
          },
        },
  ];

  const handleDismissBottomSheet = () => {
    setIsBottomSheetOpen(false);
    navigate(BuyApartmentFlowPaths.main);
  };

  const getSwitchboxProps = (label: string) => ({
    label,
    className: clsx(styles.onlyAvailable, styles.hidden),
  });

  return (
    <BaseScreen
      padContent
      title='Указать параметры продажи'
      buttons={buttons}
      onBackButtonClick={() => navigate(-1)}
    >
      <div className={styles.container}>
        <ApartmentListItem
          isFavorite={false}
          price={apartmentSellInfo?.price}
          squarePrice={apartmentSellInfo?.areaInSqM}
          image={apartmentSellInfo?.planImage ?? ''}
          rooms={apartmentSellInfo?.roomCount ?? 0}
          area={apartmentSellInfo?.areaInSqM ?? 0}
          housingEstate={selectedSellComplex.title}
          floor={
            apartmentSellInfo
              ? { floor: apartmentSellInfo.floor, total: apartmentSellInfo.floorCount }
              : { floor: 0, total: 0 }
          }
        />
        <p className={styles.description}>
          Квартиры могут отличаться по стоимости в зависимости от наличия обременений
        </p>
        <div>
          <Switchbox
            {...getSwitchboxProps('Несовершеннолетние собственники')}
            labelPosition='left'
            checked={!!minorOwners}
            onChange={() => {
              setMinorOwners(!minorOwners);
            }}
            className={styles.containerSwitchBox}
          />
        </div>
        <div className={styles.line} />
        <Switchbox
          {...getSwitchboxProps('Материнский капитал')}
          labelPosition='left'
          checked={maternalCapital}
          onChange={setMaternalCapital}
          className={styles.containerSwitchBox}
        />
        <div className={styles.line} />
        <Switchbox
          {...getSwitchboxProps('Обременение')}
          labelPosition='left'
          checked={encumbrance}
          onChange={setEncumbrance}
          className={styles.containerSwitchBox}
        />
        <div className={styles.line} />
        <Switchbox
          {...getSwitchboxProps('Занижение')}
          labelPosition='left'
          checked={Boolean(understatement)}
          onChange={setUnderstatement}
          className={styles.containerSwitchBox}
        />
        <div className={styles.line} />
        {understatement ? (
          <>
            <div>
              <span className='text-medium'>Укажите сумму занижения</span>
              <NumberInput
                sign={ruble}
                value={priceWithoutMarketPrise}
                onReset={() => {
                  setPriceWithoutMarketPrise('');
                }}
                onChange={setPriceWithoutMarketPrise}
                className={styles.priceInput}
              />
            </div>
          </>
        ) : null}
      </div>

      <BottomSheet open={isBottomSheetOpen} onDismiss={handleDismissBottomSheet}>
        <BottomSheetResult type='info' header='Заявка принята'>
          Ваша заявка отправлена на модерацию
        </BottomSheetResult>
        <Button label='Отлично' onClick={handleDismissBottomSheet} />
      </BottomSheet>
    </BaseScreen>
  );
};

export default AddAdditionalOptions;
